import React, { useState, useEffect } from 'react'
import { ArrowLeft, Send, CheckCircle2, AlertCircle, Plus, Trash2, Image } from 'lucide-react'

const LAST_CAMPAIGN_KEY = 'last_campaign_email'

const loadLastCampaign = () => {
  try {
    return JSON.parse(localStorage.getItem(LAST_CAMPAIGN_KEY)) || {}
  } catch {
    return {}
  }
}

const CampaignEmailForm = ({ onBack, selectedUserIds }) => {
  const last = loadLastCampaign()
  const [subject, setSubject] = useState(last.subject ?? '【感謝】AnytimeTracker 料金プラン改定のお知らせ＆早期登録者特典')
  const [title, setTitle] = useState(last.title ?? '料金プラン改定のお知らせ（2026/4/25〜）')
  const [sections, setSections] = useState(last.sections ?? [
    { image: 'welcome_ai_advice.png', caption: '早期登録者特典：ベーシック相当機能を¥320/月で永年ロック（あなた限定）' },
    { image: 'welcome_growth_visual.png', caption: 'プレミアム¥1,280/月ならAIコーチ・他アプリ画像丸投げ 無制限' }
  ])
  const [ctaText, setCtaText] = useState(last.ctaText ?? '設定画面を開く')
  const [ctaUrl, setCtaUrl] = useState(last.ctaUrl ?? 'https://anytime-tracker.com/settings')
  const [footerText, setFooterText] = useState(last.footerText ??
    'いつもAnytime Trackerをご利用いただきありがとうございます。開発者の武田です。<br><br>' +
    'サービスの成長に合わせ、<strong>2026年4月25日より料金プランを3種類の構成に改定</strong>します。<br>' +
    '早期からご登録いただいた皆さまには、特別価格でのご利用を永年ロックさせていただきます。<br><br>' +
    '<strong>■ 新料金プラン（2026/4/25〜）</strong><br>' +
    '・無料：トレーニング記録・カレンダー・SNS共有、AIコーチ月1回、他アプリ画像から記録を丸投げ月3枚<br>' +
    '・ベーシック ¥680/月：上記 + 分析チャート + AIコーチ月5回 + 他アプリ画像丸投げ月10枚<br>' +
    '・プレミアム ¥1,280/月：上記 + AIコーチ・他アプリ画像丸投げ 無制限 + 全機能<br><br>' +
    '※「他のアプリの画像から記録を丸投げ」は、他のトレーニング記録アプリのスクリーンショットをアップロードするだけで自動でデータを取り込める機能です（筋トレMEMO等からの移行にご活用ください）<br><br>' +
    '<strong>■ 早期登録者特典（あなた限定）</strong><br>' +
    'ベーシック相当の機能を <strong>¥320/月</strong> で永年ご利用いただけます。<br>' +
    '※AIコーチ無制限・画像丸投げ無制限をご希望の場合はプレミアム(¥1,280)へのアップグレードも可能です<br><br>' +
    '<strong>■ トライアル期間について</strong><br>' +
    '現在の2ヶ月トライアル（プレミアム機能すべて体験可能）は変更ありません。<br>' +
    'トライアル終了時に「¥320特別価格」「¥1,280プレミアム」「無料プラン」からお選びいただけます。<br><br>' +
    'ご質問・ご要望がありましたら、お気軽にご返信ください。<br><br>' +
    'AnytimeTracker 開発者 武田'
  )
  const [availableImages, setAvailableImages] = useState([])
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchImages()
  }, [])

  // 編集内容を常に保存し、次回フォームを開いたときに復元する
  useEffect(() => {
    try {
      localStorage.setItem(LAST_CAMPAIGN_KEY, JSON.stringify({ subject, title, sections, ctaText, ctaUrl, footerText }))
    } catch { /* 保存失敗時は無視 */ }
  }, [subject, title, sections, ctaText, ctaUrl, footerText])

  const fetchImages = async () => {
    try {
      const response = await fetch('/api/v1/admin/campaign_emails/images', {
        headers: { 'Content-Type': 'application/json' }
      })
      if (response.ok) {
        const data = await response.json()
        setAvailableImages(data)
      }
    } catch (err) {
      console.error('Failed to fetch images:', err)
    }
  }

  const addSection = () => {
    if (sections.length < 5) {
      setSections([...sections, { image: availableImages[0] || '', caption: '' }])
    }
  }

  const removeSection = (index) => {
    setSections(sections.filter((_, i) => i !== index))
  }

  const updateSection = (index, field, value) => {
    const updated = [...sections]
    updated[index] = { ...updated[index], [field]: value }
    setSections(updated)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(false)

    try {
      const response = await fetch('/api/v1/admin/campaign_emails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          user_ids: selectedUserIds,
          subject,
          title,
          sections,
          cta_text: ctaText,
          cta_url: ctaUrl,
          footer_text: footerText
        })
      })

      if (response.ok) {
        setSuccess(true)
      } else {
        const data = await response.json()
        setError(data.error || '送信に失敗しました')
      }
    } catch (err) {
      setError('送信に失敗しました')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const inputClass = "w-full bg-[#1c1c1e] border border-gray-800 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
  const labelClass = "block text-sm font-medium text-gray-400 mb-2"

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={onBack} className="text-gray-400 hover:text-white transition-colors">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-2xl font-bold text-white">キャンペーンメール送信</h1>
        <span className="text-sm text-gray-400">({selectedUserIds.length}名に送信)</span>
      </div>

      {success && (
        <div className="mb-6 bg-green-500/10 border border-green-500/20 text-green-500 p-4 rounded-lg flex items-center gap-3">
          <CheckCircle2 size={20} />
          送信を開始しました
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-500/10 border border-red-500/20 text-red-500 p-4 rounded-lg flex items-center gap-3">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className={labelClass}>件名</label>
          <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputClass} required disabled={loading} />
        </div>

        <div>
          <label className={labelClass}>タイトル（メール本文の見出し）</label>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} required disabled={loading} />
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-400">セクション（画像 + キャプション）</label>
            <button
              type="button"
              onClick={addSection}
              disabled={sections.length >= 5 || loading}
              className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-400 disabled:text-gray-600 disabled:cursor-not-allowed"
            >
              <Plus size={14} />
              追加
            </button>
          </div>
          <div className="space-y-4">
            {sections.map((section, index) => (
              <div key={index} className="bg-[#1c1c1e] border border-gray-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Image size={12} />
                    セクション {index + 1}
                  </span>
                  {sections.length > 1 && (
                    <button type="button" onClick={() => removeSection(index)} className="text-gray-500 hover:text-red-400" disabled={loading}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">画像</label>
                    <select
                      value={section.image}
                      onChange={(e) => updateSection(index, 'image', e.target.value)}
                      className={inputClass}
                      disabled={loading}
                    >
                      {availableImages.map((img) => (
                        <option key={img} value={img}>{img}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">キャプション</label>
                    <input
                      type="text"
                      value={section.caption}
                      onChange={(e) => updateSection(index, 'caption', e.target.value)}
                      className={inputClass}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className={labelClass}>CTAボタンのテキスト</label>
          <input type="text" value={ctaText} onChange={(e) => setCtaText(e.target.value)} className={inputClass} required disabled={loading} />
        </div>

        <div>
          <label className={labelClass}>CTAボタンのリンク先URL</label>
          <input type="text" value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} className={inputClass} required disabled={loading} />
        </div>

        <div>
          <label className={labelClass}>フッターテキスト（HTML可）</label>
          <textarea
            value={footerText}
            onChange={(e) => setFooterText(e.target.value)}
            className={`${inputClass} min-h-[80px]`}
            disabled={loading}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <Send size={20} />
          {loading ? '送信中...' : `${selectedUserIds.length}名に送信`}
        </button>
      </form>
    </div>
  )
}

export default CampaignEmailForm
